import * as React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface PasoStepper {
  clave: string
  titulo: string
  /** Una línea de qué pasa en este paso. En móvil solo se ve la del paso actual. */
  descripcion?: string
  /** Un paso puede quedar mal sin bloquear el avance: el punto sale en rojo. */
  conError?: boolean
}

export interface StepperProps {
  pasos: readonly PasoStepper[]
  /** Clave del paso en curso. */
  actual: string
  /** Volver atrás a un paso ya hecho. Sin esto, el stepper es solo decoración. */
  onIr?: (clave: string) => void
  /** Pasos a los que se puede saltar aunque no se hayan hecho todavía. */
  habilitados?: readonly string[]
  className?: string
}

/**
 * Los pasos de un proceso largo, con el que va en curso destacado.
 *
 * Por qué existe: hay tres asistentes en el portal (onboarding, cotización, migración) y
 * cada uno pintaba sus pasos a mano, con un aspecto distinto y sin poder volver atrás. Un
 * asistente en el que no se puede retroceder obliga a cancelar y empezar de cero cada vez
 * que el asesor se equivoca de casilla.
 *
 * Nace con DOS consumidores a la vez (onboarding y migración) a propósito: un componente
 * compartido con un solo consumidor no es compartido, es una capa de más.
 *
 * En móvil se compacta a "Paso 2 de 5 · Preparar" con la barra debajo. Cinco círculos con
 * texto no caben en 375 px, y lo que se rompe no es el diseño: es la única señal de por
 * dónde va el proceso.
 */
export function Stepper({ pasos, actual, onIr, habilitados, className }: StepperProps) {
  const iActual = Math.max(0, pasos.findIndex((p) => p.clave === actual))
  const puedeIr = (i: number, paso: PasoStepper): boolean =>
    Boolean(onIr) && (i <= iActual || (habilitados?.includes(paso.clave) ?? false))

  return (
    <nav aria-label="Pasos" className={cn('w-full', className)}>
      {/* Móvil */}
      <div className="sm:hidden">
        <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
          Paso {iActual + 1} de {pasos.length}
          {' · '}
          <span className="font-medium text-neutral-900 dark:text-neutral-50">{pasos[iActual]?.titulo}</span>
        </p>
        {pasos[iActual]?.descripcion && (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">{pasos[iActual]?.descripcion}</p>
        )}
        <div className="mt-2 flex gap-1" aria-hidden="true">
          {pasos.map((p, i) => (
            <span
              key={p.clave}
              className={cn(
                'h-1 flex-1 rounded-full',
                p.conError ? 'bg-danger' : i <= iActual ? 'bg-primary' : 'bg-neutral-200 dark:bg-neutral-700'
              )}
            />
          ))}
        </div>
      </div>

      {/* Escritorio */}
      <ol className="hidden sm:flex sm:items-start">
        {pasos.map((paso, i) => {
          const hecho = i < iActual
          const enCurso = i === iActual
          const navegable = puedeIr(i, paso)
          return (
            <li key={paso.clave} className="flex flex-1 items-start last:flex-none">
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  disabled={!navegable}
                  onClick={() => navegable && onIr?.(paso.clave)}
                  aria-current={enCurso ? 'step' : undefined}
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-caption font-semibold transition-colors',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                    paso.conError
                      ? 'border-danger bg-danger-50 dark:bg-danger/15 text-danger-700 dark:text-danger-300'
                      : hecho
                        ? 'border-primary bg-primary text-white'
                        : enCurso
                          ? 'border-primary bg-card text-primary-700 dark:text-primary-300'
                          : 'border-neutral-300 dark:border-neutral-700 bg-card text-neutral-400 dark:text-neutral-500',
                    navegable ? 'cursor-pointer hover:border-primary-400 dark:hover:border-primary-600' : 'cursor-default'
                  )}
                >
                  {hecho && !paso.conError ? <Check size={16} aria-hidden="true" /> : i + 1}
                </button>
                <div className="mt-1.5 max-w-[10rem] text-center">
                  <p
                    className={cn(
                      'text-caption font-medium',
                      enCurso ? 'text-neutral-900 dark:text-neutral-50' : hecho ? 'text-neutral-700 dark:text-neutral-300' : 'text-neutral-400 dark:text-neutral-500'
                    )}
                  >
                    {paso.titulo}
                  </p>
                  {paso.descripcion && enCurso && (
                    <p className="text-caption text-neutral-500 dark:text-neutral-400">{paso.descripcion}</p>
                  )}
                </div>
              </div>
              {i < pasos.length - 1 && (
                <div
                  aria-hidden="true"
                  className={cn('mx-2 mt-4 h-0.5 flex-1', i < iActual ? 'bg-primary' : 'bg-neutral-200 dark:bg-neutral-700')}
                />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export default Stepper
